import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_current_participation_table';

export const ASSET_CURRENT_PARTICIPATION_QUERY = gql`
  query assetCurrentParticipation($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      currentParticipations {
        id
        isCompanyOperator
        formattedPercentStake
        company {
          id
          name
        }
      }
    }
  }
`;

interface AssetCurrentParticipationRow {
  label: string;
  value: string;
}

const formatParticipations = (asset: Asset | null | undefined): AssetCurrentParticipationRow[] => {
  if (!asset?.currentParticipations?.length) {
    return [{ label: 'No current participations available', value: '' }];
  }

  return asset.currentParticipations
    .filter(participation => participation?.company?.name)
    .map(participation => {
      const companyName = participation?.company?.name ?? 'Unknown Company';
      const operatorSuffix = participation?.isCompanyOperator ? ' (Operator)' : '';
      const value = participation?.formattedPercentStake ?? '';

      return {
        label: `${companyName}${operatorSuffix}`,
        value,
      };
    });
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery<{ assetV2: Asset }>(ASSET_CURRENT_PARTICIPATION_QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData || !assetId,
    onCompleted: response => {
      if (response?.assetV2) {
        const data = response.assetV2;
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched ?? ({} as Asset), error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<Asset>) => {
  const formattedData = formatParticipations(data);
  const rows = formattedData?.length
    ? formattedData
    : [{ label: 'No current participations available', value: '' }];

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
      data-testid='asset-current-participation-table'
    >
      <ComponentTable>
        <ComponentTableHeader>Participation</ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      <ComponentView data={data} />
    </LoadingErrorWrapper>
  );
};

export const AssetCurrentParticipationTableComponent: ComponentDefinition<Asset> = {
  id: COMPONENT_ID,
  name: 'Asset Current Participation',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
